/**
 * 后台内容骨架
 * ------------------------------------------------------------------
 * 两种内容模型并存（平滑迁移）：
 *  1) FIELDS（新，主推）：每个页面声明「按区块分组的可编辑字段」，带中文 label / 类型 / 默认值。
 *     后台直接展示页面当前值，用户改当前值即生效。前端用 $C(scope, key, default) 读取。
 *     每个 scope 的字段定义放在 src/cms/fields/<scope>.js，本文件统一组装。
 *     未迁移的页面其 fields 模块 export default null，走旧机制。
 *  2) 旧机制兼容：未迁移的页面仍可用「文案替换 / 资源锚点 / 通用数据」三块编辑。
 *
 * 字段类型 type：text | textarea | number | image | video | object | array
 * 文字、图片、视频、数据四类全部走 fields 存储（统一模型），前端 $C 读取。
 */

// 各页面字段模型（未迁移的页 export default null，自动回落旧机制）
import productResearch from './fields/productResearch'
import index from './fields/index'
import international from './fields/international'
import china from './fields/china'
import ganzi from './fields/ganzi'
import sell from './fields/sell'
import downstreamHeat from './fields/downstreamHeat'
import companyDetail from './fields/companyDetail'
import qulaAcquisition from './fields/qulaAcquisition'
import industryChain from './fields/industryChain'
import safetyFlow from './fields/safetyFlow'
import safetyDevice from './fields/safetyDevice'
import processFlow from './fields/processFlow'
import processDemo from './fields/processDemo'
import common from './fields/common'

/** 页面清单：后台左侧导航按组展示 */
export const PAGES = [
  { scope: 'index', title: '首页 · 全球分布', group: '主屏' },
  { scope: 'international', title: '国际市场', group: '主屏' },
  { scope: 'china', title: '国内市场', group: '主屏' },
  { scope: 'ganzi', title: '甘孜市场', group: '主屏' },
  { scope: 'sell', title: '康巴圣德经营数据', group: '甘孜市场' },
  { scope: 'downstreamHeat', title: '下游客户热力图', group: '甘孜市场' },
  { scope: 'companyDetail', title: '下游企业详情', group: '甘孜市场' },
  { scope: 'qulaAcquisition', title: '曲拉收购可视化', group: '甘孜市场' },
  { scope: 'industryChain', title: '酪蛋白产业链数字化', group: '产业链' },
  { scope: 'productResearch', title: '产品研发数字化', group: '产业链' },
  { scope: 'safetyFlow', title: '生产安全 · 设备总览', group: '产业链' },
  { scope: 'safetyDevice', title: '生产安全 · 设备监控', group: '产业链' },
  { scope: 'processFlow', title: '工艺流程总览', group: '产业链' },
  { scope: 'processDemo', title: '工序演示视频', group: '产业链' },
  { scope: 'common', title: '公共外观（导航/标题/AI）', group: '全局' },
]

/** 全局资源锚点（旧机制，common 页仍使用） */
export const ASSET_ANCHORS = [
  { scope: 'common', key: '--cms-img-index-bg', label: '首页背景图', hint: '首页底图，建议 7680×2160 深色科技风', accept: 'image' },
  { scope: 'common', key: '--cms-img-market-bg', label: '市场页通用背景', hint: '国际/国内/甘孜市场共用底图', accept: 'image' },
  { scope: 'common', key: '--cms-img-top', label: '顶部标题装饰条', hint: '页面顶部渐变条背景', accept: 'image' },
  { scope: 'common', key: '--cms-img-bottom', label: '底部导航底板', hint: '底部市场切换导航的底图', accept: 'image' },
  { scope: 'common', key: '--cms-img-back-icon', label: '返回按钮图标', hint: '左上角返回箭头', accept: 'image' },
  { scope: 'common', key: '--cms-video-ai', label: 'AI 数字人待机视频', hint: 'AI 对话助手默认形象，支持 mp4/webm', accept: 'video' },
  { scope: 'common', key: '--cms-video-ai-speak', label: 'AI 数字人讲话视频', hint: 'AI 回答时的口播形象', accept: 'video' },
]

/** 新字段模型：每个 scope 一个模块；null 表示尚未迁移（回落旧机制） */
export const FIELDS = {
  productResearch,
  index,
  international,
  china,
  ganzi,
  sell,
  downstreamHeat,
  companyDetail,
  qulaAcquisition,
  industryChain,
  safetyFlow,
  safetyDevice,
  processFlow,
  processDemo,
  common,
}

/** 该页面是否已迁移到新字段模型（返回字段定义对象或 null） */
export function fieldsModel(scope) {
  const m = FIELDS[scope]
  return m && m.groups ? m : null
}

/** 兼容旧资源锚点查询 */
export function anchorsOf(scope) {
  return ASSET_ANCHORS.filter(a => a.scope === scope)
}

export function pageTitle(scope) {
  const p = PAGES.find(x => x.scope === scope)
  return p ? p.title : scope
}

export function groupedPages() {
  const out = []
  for (const p of PAGES) {
    let g = out.find(x => x.group === p.group)
    if (!g) {
      g = { group: p.group, items: [] }
      out.push(g)
    }
    g.items.push(p)
  }
  return out
}

export default { PAGES, ASSET_ANCHORS, FIELDS, fieldsModel, anchorsOf, pageTitle, groupedPages }
